import { useMemo, useState } from 'react';
import { useStore } from '@/store/useStore';
import { ClipboardCheck, Download, Building2, X, Info } from 'lucide-react';
import { cn } from '@/utils/cn';
import { downloadTableExcel } from '@/utils/exportTable';
import { useTzShiftMs } from '@/hooks/useTzShift';
import { DEAL_TO_FUNNEL, type Deal, type ID, type Team } from '@/types';

/**
 * «Заполняемость» — насколько аккуратно филиалы заполняют данные по УЖЕ ПРОВЕДЁННЫМ играм.
 *
 * Зачем: автоматический расчёт зарплаты линейного персонала возможен только по играм, где
 * отмечено, КТО работал (и сколько времени). Пока состав не отмечен — считать нечего.
 * Экран показывает картину по филиалам за месяц, чтобы руководитель сети спрашивал не
 * «вообще», а с конкретного города и по конкретным играм (клик по проценту → список).
 *
 * Считаем на клиенте по уже загруженным сделкам (как вкладка «Загрузка» в Персонале) —
 * сервер и база не трогаются.
 */

type MetricKey = 'staff' | 'participants' | 'end' | 'format';

const METRICS: { key: MetricKey; label: string; short: string; hint: string }[] = [
  { key: 'staff', label: 'Состав отмечен', short: 'Состав', hint: 'Кто работал на игре: поимённо или назначена команда с участниками' },
  { key: 'participants', label: 'Кол-во участников', short: 'Участники', hint: 'Заполнено поле «Кол-во участников» в карточке игры' },
  { key: 'end', label: 'Время окончания', short: 'Окончание', hint: 'Проставлено время окончания игры' },
  { key: 'format', label: 'Формат игры', short: 'Формат', hint: 'Выбран формат игры (Классика, Тимбилдинг и т.д.)' },
];

/** Пустой филиал в группировке — чтобы игры без города не потерялись из счёта. */
const NO_BRANCH = '_none';

const MONTHS_RU = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];

/** «2026-08» → «Август 2026». */
function monthLabel(month: string): string {
  const [y, m] = month.split('-').map(Number);
  if (!y || !m) return month;
  return `${MONTHS_RU[m - 1] ?? m} ${y}`;
}

function ddmmyy(ts: number): string {
  const d = new Date(ts);
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getFullYear()).slice(2)}`;
}

/** Название игры для списка: своё название события, иначе имя клиента, иначе номер. */
function gameTitle(d: Deal): string {
  return (d.event.title || '').trim() || (d.contact_name || '').trim() || `#${d.number}`;
}

/**
 * Заполнено ли поле у конкретной игры.
 * «Состав» считаем заполненным, если есть хотя бы одно поимённое назначение ИЛИ
 * назначена команда, в которой есть участники (в филиалах пользуются и тем, и другим).
 */
function isFilled(key: MetricKey, d: Deal, teamById: Map<string, Team>): boolean {
  switch (key) {
    case 'staff': {
      const named = (d.staff_assignments ?? []).some((s) => !!s.employee_id);
      if (named) return true;
      const team = d.team_id ? teamById.get(d.team_id) : null;
      return !!team && team.member_ids.length > 0;
    }
    case 'participants':
      return (d.event.participant_count_text ?? '').trim() !== '';
    case 'end':
      return d.event.end_at != null;
    case 'format':
      return !!d.event.game_format_id;
  }
}

/** Цветовые токены проекта: <50 % — красный, 50–80 % — жёлтый, >80 % — зелёный. */
function pctTone(pct: number): { text: string; bg: string } {
  if (pct < 50) return { text: 'text-status-refused', bg: 'bg-status-refused/12' };
  if (pct <= 80) return { text: 'text-status-partial', bg: 'bg-status-partial/15' };
  return { text: 'text-status-paid', bg: 'bg-status-paid/12' };
}

type Row = {
  id: string;
  name: string;
  color: string | null;
  total: number;
  filled: Record<MetricKey, number>;
  missing: Record<MetricKey, Deal[]>;
};

const emptyCounts = (): Record<MetricKey, number> => ({ staff: 0, participants: 0, end: 0, format: 0 });
const emptyMissing = (): Record<MetricKey, Deal[]> => ({ staff: [], participants: [], end: [], format: [] });

export function FillRatePage() {
  const { deals, teams, branches, filterBranchId, setModal, toast } = useStore();
  const tzShiftMs = useTzShiftMs();

  const [month, setMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });

  const branchById = useMemo(() => new Map(branches.map((b) => [b.id, b])), [branches]);
  const teamById = useMemo(() => new Map(teams.map((t) => [t.id, t])), [teams]);

  const rows = useMemo<Row[]>(() => {
    const [y, m] = month.split('-').map(Number);
    if (!y || !m) return [];
    const monthStart = new Date(y, m - 1, 1).getTime();
    const monthEnd = new Date(y, m, 1).getTime();

    const acc = new Map<string, Row>();

    for (const d of deals) {
      // Только проведённые игры: незакрытую игру не за что упрекать.
      const fs = d.funnel_status ?? DEAL_TO_FUNNEL[d.status];
      if (fs !== 'done') continue;
      // Отбор по месяцу — по дате события (как во вкладке «Загрузка»).
      const dt = d.event.start_at ?? d.event.event_date;
      if (dt == null || dt < monthStart || dt >= monthEnd) continue;
      // Верхний выбор филиала в шапке уважаем, как и остальные экраны.
      if (filterBranchId !== 'all' && d.branch_id !== filterBranchId) continue;

      const key = d.branch_id ?? NO_BRANCH;
      let row = acc.get(key);
      if (!row) {
        const b = branchById.get(key);
        row = { id: key, name: b?.name ?? 'Без филиала', color: b?.color ?? null, total: 0, filled: emptyCounts(), missing: emptyMissing() };
        acc.set(key, row);
      }
      row.total += 1;
      for (const met of METRICS) {
        if (isFilled(met.key, d, teamById)) row.filled[met.key] += 1;
        else row.missing[met.key].push(d);
      }
    }

    const out = Array.from(acc.values());
    // Внутри филиала пропуски идут по дате — так удобнее идти по списку сверху вниз.
    for (const r of out) {
      for (const met of METRICS) {
        r.missing[met.key].sort((a, b) => (a.event.start_at ?? a.event.event_date ?? 0) - (b.event.start_at ?? b.event.event_date ?? 0));
      }
    }
    // Худшие — сверху: экран нужен, чтобы сразу видеть, с кого спрашивать.
    const avg = (r: Row) => METRICS.reduce((s, met) => s + (r.total ? r.filled[met.key] / r.total : 0), 0) / METRICS.length;
    return out.sort((a, b) => avg(a) - avg(b) || b.total - a.total || a.name.localeCompare(b.name));
  }, [deals, month, filterBranchId, branchById, teamById]);

  const totals = useMemo(() => {
    const total = rows.reduce((s, r) => s + r.total, 0);
    const filled = emptyCounts();
    for (const r of rows) for (const met of METRICS) filled[met.key] += r.filled[met.key];
    return { total, filled };
  }, [rows]);

  const branchLabel = filterBranchId === 'all' ? 'Все филиалы' : (branchById.get(filterBranchId)?.name ?? 'Филиал');

  const exportExcel = () => {
    const head = ['Филиал', 'Проведено игр', ...METRICS.map((m) => `${m.label}, %`), ...METRICS.map((m) => `${m.label}: не заполнено`)];
    const pct = (f: number, t: number) => (t > 0 ? Math.round((f / t) * 100) : 0);
    const body: unknown[][] = rows.map((r) => [
      r.name,
      r.total,
      ...METRICS.map((m) => pct(r.filled[m.key], r.total)),
      ...METRICS.map((m) => r.missing[m.key].length),
    ]);
    body.push([
      'ИТОГО',
      totals.total,
      ...METRICS.map((m) => pct(totals.filled[m.key], totals.total)),
      ...METRICS.map((m) => totals.total - totals.filled[m.key]),
    ]);
    downloadTableExcel(`Заполняемость-${month}.xlsx`, head, body);
    toast({ kind: 'success', text: 'Заполняемость выгружена' });
  };

  // Список игр с пропуском (открывается кликом по проценту).
  const [gapModal, setGapModal] = useState<{ branch: string; metric: MetricKey; items: Deal[] } | null>(null);
  const openGapDeal = (id: ID) => {
    const nav = gapModal?.items.map((d) => d.id);
    setGapModal(null);
    setModal({ kind: 'deal', id }, nav);
  };
  const openGaps = (r: Row, key: MetricKey) => {
    if (r.missing[key].length === 0) return;
    setGapModal({ branch: r.name, metric: key, items: r.missing[key] });
  };

  return (
    <div className="h-full flex flex-col">
      {/* Панель управления */}
      <div className="min-h-14 px-3 sm:px-5 py-2 sm:py-0 flex items-center flex-wrap gap-3 border-b border-line bg-surface-1 flex-shrink-0">
        <div className="flex items-center gap-2 font-semibold text-ink-1">
          <ClipboardCheck className="w-4 h-4 text-accent" /> Заполняемость
        </div>
        <span className="text-xs text-ink-3 flex items-center gap-1"><Building2 className="w-3.5 h-3.5" />{branchLabel}</span>

        <div className="flex-1" />

        <input
          type="month"
          className="input h-9 w-[160px]"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          title="Месяц: считаем проведённые игры этого месяца"
        />
        <button className="btn-soft" onClick={exportExcel} disabled={rows.length === 0}
          title="Выгрузить таблицу заполняемости в Excel">
          <Download className="w-4 h-4" /> Excel
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-3 sm:p-5">
        <div className="max-w-[1100px] mx-auto">
          <div className="flex items-start gap-2 mb-3 text-xs text-ink-3">
            <Info className="w-4 h-4 flex-shrink-0 mt-px" />
            <p>
              Проценты считаются по проведённым играм за {monthLabel(month)}. Клик по проценту — список игр,
              где поле не заполнено. Данные нужны для автоматического расчёта зарплаты: без отмеченного состава
              игра в расчёт не попадёт.
            </p>
          </div>

          {rows.length === 0 ? (
            <div className="panel p-8 text-center">
              <ClipboardCheck className="w-10 h-10 text-ink-3 mx-auto mb-2" />
              <p className="text-sm text-ink-2">За этот месяц проведённых игр нет</p>
            </div>
          ) : (
            <div className="panel p-0 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-ink-3 border-b border-line">
                      <th className="px-2 sm:px-4 py-2.5 font-medium min-w-[130px]">Филиал</th>
                      <th className="px-2 sm:px-4 py-2.5 font-medium text-right w-[70px] sm:w-[92px]">Игр</th>
                      {METRICS.map((m) => (
                        <th key={m.key} className="px-2 sm:px-3 py-2.5 font-medium text-center w-[92px] sm:w-[124px]" title={m.hint}>
                          {m.short}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.id} className="border-b border-line/50 hover:bg-surface-2/40">
                        <td className="px-2 sm:px-4 py-2.5">
                          <span className="flex items-center gap-2 min-w-0">
                            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: r.color ?? 'rgb(var(--ink-3))' }} />
                            <span className="font-semibold text-ink-1 truncate">{r.name}</span>
                          </span>
                        </td>
                        <td className="px-2 sm:px-4 py-2.5 text-right text-ink-1 tabular-nums font-semibold">{r.total}</td>
                        {METRICS.map((m) => (
                          <PctCell key={m.key} filled={r.filled[m.key]} total={r.total}
                            onClick={() => openGaps(r, m.key)} />
                        ))}
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-line font-bold text-ink-1 bg-surface-2/50">
                      <td className="px-2 sm:px-4 py-2.5 truncate">ИТОГО · {rows.length} фил.</td>
                      <td className="px-2 sm:px-4 py-2.5 text-right tabular-nums">{totals.total}</td>
                      {METRICS.map((m) => (
                        <PctCell key={m.key} filled={totals.filled[m.key]} total={totals.total} />
                      ))}
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {gapModal && (
        <GapGamesModal
          branch={gapModal.branch}
          metric={gapModal.metric}
          items={gapModal.items}
          periodText={monthLabel(month)}
          tzShiftMs={tzShiftMs}
          onOpenDeal={openGapDeal}
          onClose={() => setGapModal(null)}
        />
      )}
    </div>
  );
}

/** Ячейка с процентом: цвет по порогам, под ней — «сколько игр без поля». */
function PctCell({ filled, total, onClick }: { filled: number; total: number; onClick?: () => void }) {
  const pct = total > 0 ? Math.round((filled / total) * 100) : 0;
  const gaps = total - filled;
  const tone = pctTone(pct);
  const clickable = !!onClick && gaps > 0;
  return (
    <td className="px-2 sm:px-3 py-2 text-center">
      <button
        type="button"
        disabled={!clickable}
        onClick={clickable ? onClick : undefined}
        title={clickable ? `Показать игры без данных (${gaps})` : 'Все игры заполнены'}
        className={cn('inline-flex flex-col items-center justify-center min-w-[64px] px-2 py-1 rounded-lg tabular-nums transition-colors',
          tone.bg, tone.text,
          clickable ? 'cursor-pointer hover:brightness-95 hover:ring-1 hover:ring-current/40' : 'cursor-default')}
      >
        <span className="font-bold leading-tight">{pct}%</span>
        <span className="text-[10px] opacity-80 leading-tight">{gaps > 0 ? `−${gaps}` : 'все'}</span>
      </button>
    </td>
  );
}

/**
 * Список игр филиала, у которых конкретное поле не заполнено. Клик по строке открывает
 * карточку игры; стрелки «след./пред.» в карточке ходят по этому же списку (navIds) —
 * чтобы управляющий закрыл все пропуски подряд, не возвращаясь в таблицу.
 */
function GapGamesModal({ branch, metric, items, periodText, tzShiftMs, onOpenDeal, onClose }: {
  branch: string; metric: MetricKey; items: Deal[];
  periodText: string; tzShiftMs: number;
  onOpenDeal: (id: ID) => void; onClose: () => void;
}) {
  const met = METRICS.find((m) => m.key === metric)!;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-in">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-[820px] max-h-[92vh] flex flex-col panel shadow-pop animate-pop">
        <div className="flex items-start justify-between gap-4 p-5 border-b border-line">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl grid place-items-center flex-shrink-0 bg-status-refused/12 text-status-refused">
              <ClipboardCheck className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <h3 className="text-lg font-bold text-ink-1 leading-tight truncate">Не заполнено: {met.label.toLowerCase()}</h3>
              <p className="text-xs text-ink-3 mt-0.5 truncate">{branch} · {periodText} · {items.length} игр</p>
            </div>
          </div>
          <button onClick={onClose} className="icon-btn !w-9 !h-9 rounded-full flex-shrink-0"><X className="w-4 h-4" /></button>
        </div>
        <div className="overflow-auto p-2 sm:p-3">
          {items.length === 0 ? (
            <div className="py-10 text-center text-ink-3 italic">Пропусков нет</div>
          ) : (
            <div className="min-w-[560px] sm:min-w-0 divide-y divide-line/60">
              {items.map((d, i) => {
                const ts = d.event.start_at ?? d.event.event_date;
                return (
                  <button key={d.id} onClick={() => onOpenDeal(d.id)}
                    className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-surface-2 transition-colors text-left rounded-lg">
                    <span className="text-xs text-ink-3 tabular-nums w-6 flex-shrink-0">{i + 1}</span>
                    <span className="text-xs text-ink-2 tabular-nums w-[68px] flex-shrink-0">{ts != null ? ddmmyy(ts + tzShiftMs) : '—'}</span>
                    <span className="text-sm text-accent font-medium tabular-nums w-[72px] flex-shrink-0">#{d.number}</span>
                    <span className="text-sm text-ink-1 flex-1 truncate">{gameTitle(d)}</span>
                    <span className="text-xs text-ink-3 w-[150px] flex-shrink-0 truncate hidden sm:block">{(d.contact_name || '').trim() || '—'}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-line text-[11px] text-ink-3">
          {met.hint}. Откройте игру и заполните поле — процент пересчитается сам.
        </div>
      </div>
    </div>
  );
}
